import React, { useState, useEffect, useMemo } from 'react'
import { apiClient } from '@/utils/api'
import { useAuth } from '@/hooks/useAuth'
import toast from 'react-hot-toast'
import { RotateCcw, Users, Award, AlertCircle, Undo2, Edit2, Calendar, Filter, ArrowUpDown, Clock, CheckCircle2, CalendarDays } from 'lucide-react'
import { format, startOfDay, endOfDay, subDays, isWithinInterval, parseISO } from 'date-fns'
import { parseDueDate } from '@/utils/timezoneUtils'
import {
  useAssignments,
  useVerifyAssignment,
  useUnverifyAssignment,
  useUpdateAssignmentStatus,
} from '@/hooks/useAssignments'

interface Assignment {
  id: string
  chore_id: string
  assigned_to: string
  status: string
  points_offered: number
  points_earned?: number
  due_date: string
  created_at: string
  claimed_at: string
  started_at: string
  completed_at: string
  completion_notes: string
  completion_photo_url: string
  verification_notes?: string
  verified_at?: string
  assigned_user: {
    id: string
    username: string
    display_name: string
    color_theme: string
  }
  chore: {
    id: string
    name: string
    description: string
    instructions: string
    category: string
    base_points: number
    estimated_minutes: number
    difficulty: string
    requires_verification: boolean
    requires_photo: boolean
    icon: string
    tags: string[]
  }
}

interface User {
  id: string
  username: string
  display_name: string
  is_parent: boolean
  color_theme: string
}

const difficultyColors = {
  easy: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800', 
  hard: 'bg-red-100 text-red-800',
  expert: 'bg-purple-100 text-purple-800'
}

const categoryColors = {
  routine: 'bg-blue-50 border-blue-200',
  open: 'bg-green-50 border-green-200',
  premium: 'bg-purple-50 border-purple-200'
}

interface Props {
  onVerificationComplete?: () => void
}

export default function EnhancedChoreVerification({ onVerificationComplete }: Props = {}) {
  // Use React Query for data fetching with automatic cache invalidation
  const { data: pendingVerificationData = [], isLoading: pendingLoading } = useAssignments({
    status: 'pending_verification',
    dateFilter: 'all',
    limit: 500
  })
  const { data: completedData = [], isLoading: completedLoading } = useAssignments({
    status: 'completed',
    dateFilter: 'all',
    limit: 500
  })
  const { data: verifiedData = [], isLoading: verifiedLoading } = useAssignments({
    status: 'verified',
    dateFilter: 'month',
    limit: 100
  })

  // Mutations with automatic cache invalidation
  const verifyMutation = useVerifyAssignment()
  const unverifyMutation = useUnverifyAssignment()
  const updateStatusMutation = useUpdateAssignmentStatus()

  // Combine pending verifications from both sources
  const pendingVerifications = useMemo(() => {
    const fromPendingVerification = pendingVerificationData.filter(
      (a: Assignment) => a.status === 'pending_verification' && !a.verified_at
    )
    const fromCompleted = completedData.filter(
      (a: Assignment) => a.status === 'completed' && !a.verified_at
    )
    return [...fromPendingVerification, ...fromCompleted]
  }, [pendingVerificationData, completedData])

  // Filter verified history
  const verificationHistory = useMemo(() => {
    return verifiedData.filter((a: Assignment) => a.status === 'verified' && a.verified_at).slice(0, 20)
  }, [verifiedData])

  const isLoading = pendingLoading || completedLoading || verifiedLoading

  const [filteredHistory, setFilteredHistory] = useState<Assignment[]>([])
  const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending')
  const [verifyingId, setVerifyingId] = useState<string>('')
  const [verificationNotes, setVerificationNotes] = useState<{ [key: string]: string }>({})
  const [pointsAdjustments, setPointsAdjustments] = useState<{ [key: string]: number }>({})
  const [adjustmentReasons, setAdjustmentReasons] = useState<{ [key: string]: string }>({})
  const [showPointAdjuster, setShowPointAdjuster] = useState<{ [key: string]: boolean }>({})
  const [showSplitPoints, setShowSplitPoints] = useState<{ [key: string]: boolean }>({})
  const [splitWithUser, setSplitWithUser] = useState<{ [key: string]: string }>({})
  const [splitPercentage, setSplitPercentage] = useState<{ [key: string]: number }>({})
  const [allKids, setAllKids] = useState<User[]>([])
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'month' | 'all'>('week')
  const [userFilter, setUserFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState<'verified_date' | 'due_date' | 'points' | 'user'>('verified_date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [editingId, setEditingId] = useState<string>('')
  const [editPoints, setEditPoints] = useState<{ [key: string]: number }>({})
  const [editNotes, setEditNotes] = useState<{ [key: string]: string }>({})
  const [editAssignedTo, setEditAssignedTo] = useState<{ [key: string]: string }>({})
  const [editStatus, setEditStatus] = useState<{ [key: string]: string }>({})
  const [editCompletionNotes, setEditCompletionNotes] = useState<{ [key: string]: string }>({})
  const { user } = useAuth()

  useEffect(() => {
    if (user?.is_parent) {
      loadAllKids()
    }
  }, [user])

  useEffect(() => {
    filterHistory()
  }, [verificationHistory, dateFilter, userFilter, sortBy, sortOrder])

  const loadAllKids = async () => {
    try {
      const users = await apiClient.getUsers()
      const kids = users.filter((u: User) => !u.is_parent)
      setAllKids(kids)
    } catch (error) {
      console.error('Failed to load kids:', error)
    }
  }

  const filterHistory = () => {
    if (!verificationHistory.length) {
      setFilteredHistory([])
      return
    }

    let filtered = [...verificationHistory]
    const now = new Date()
    
    // Apply user filter
    if (userFilter !== 'all') {
      filtered = filtered.filter(a => a.assigned_to === userFilter)
    }
    
    // Apply date filter
    switch (dateFilter) {
      case 'today':
        filtered = filtered.filter(a => {
          const verifiedDate = a.verified_at ? new Date(a.verified_at) : null
          return verifiedDate && isWithinInterval(verifiedDate, {
            start: startOfDay(now),
            end: endOfDay(now)
          })
        })
        break
      case 'week':
        filtered = filtered.filter(a => {
          const verifiedDate = a.verified_at ? new Date(a.verified_at) : null
          return verifiedDate && isWithinInterval(verifiedDate, {
            start: startOfDay(subDays(now, 7)),
            end: endOfDay(now)
          })
        })
        break
      case 'month':
        filtered = filtered.filter(a => {
          const verifiedDate = a.verified_at ? new Date(a.verified_at) : null
          return verifiedDate && isWithinInterval(verifiedDate, {
            start: startOfDay(subDays(now, 30)),
            end: endOfDay(now)
          })
        })
        break
      case 'all':
        // No filtering needed
        break
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0
      
      switch (sortBy) {
        case 'verified_date':
          const dateA = a.verified_at ? new Date(a.verified_at).getTime() : 0
          const dateB = b.verified_at ? new Date(b.verified_at).getTime() : 0
          comparison = dateA - dateB
          break
        case 'due_date':
          const dueA = a.due_date ? parseDueDate(a.due_date).getTime() : 0
          const dueB = b.due_date ? parseDueDate(b.due_date).getTime() : 0
          comparison = dueA - dueB
          break
        case 'points':
          comparison = (a.points_earned || a.points_offered) - (b.points_earned || b.points_offered)
          break
        case 'user':
          comparison = (a.assigned_user?.display_name || '').localeCompare(b.assigned_user?.display_name || '')
          break
      }
      
      return sortOrder === 'asc' ? comparison : -comparison
    })
    
    setFilteredHistory(filtered)
  }


  const handleVerify = async (assignmentId: string, approved: boolean) => {
    setVerifyingId(assignmentId)

    try {
      const assignment = pendingVerifications.find(a => a.id === assignmentId)

      if (!assignment) {
        toast.error('Assignment not found')
        setVerifyingId('')
        return
      }

      const basePoints = assignment.points_offered || 0
      // Use ?? instead of || to handle explicit 0 values correctly
      const adjustedPoints = pointsAdjustments[assignmentId] ?? basePoints
      const reason = adjustmentReasons[assignmentId]

      // Handle split points if enabled
      if (showSplitPoints[assignmentId] && splitWithUser[assignmentId]) {
        const otherUserId = splitWithUser[assignmentId]
        const percentage = splitPercentage[assignmentId] || 50
        const originalPoints = Math.floor(adjustedPoints * (percentage / 100))
        const otherPoints = adjustedPoints - originalPoints

        // Verify original assignment with split points
        await verifyMutation.mutateAsync({
          assignmentId,
          approved,
          verificationNotes: `Split points: ${percentage}% (${originalPoints} pts) to original, ${100-percentage}% (${otherPoints} pts) to helper. ${verificationNotes[assignmentId] || ''}`,
          pointsAwarded: originalPoints
        })

        // Create assignment for the helper
        if (approved) {
          await apiClient.post('/api/assignments/create-helper', {
            chore_id: assignment?.chore_id,
            assigned_to: otherUserId,
            points_offered: otherPoints,
            notes: `Helper credit for assisting with: ${assignment?.chore.name}`,
            auto_verify: true
          })

          toast.success(`Points split! ${originalPoints} to original, ${otherPoints} to helper`)
        }
      } else {
        // Regular verification
        let finalNotes = verificationNotes[assignmentId] || ''
        if (adjustedPoints !== basePoints && reason) {
          finalNotes = `Points adjusted: ${reason}${finalNotes ? '\n' + finalNotes : ''}`
        }

        await verifyMutation.mutateAsync({
          assignmentId,
          approved,
          verificationNotes: finalNotes,
          pointsAwarded: adjustedPoints
        })

        if (approved) {
          const pointsMsg = adjustedPoints === basePoints
            ? `${adjustedPoints} points awarded!`
            : `${adjustedPoints} points awarded (was ${basePoints})`
          toast.success(`Chore approved! ${pointsMsg}`)
        } else {
          toast.success('Chore rejected. Child will be notified to redo it.')
        }
      }

      // Clear form data
      setVerificationNotes(prev => ({ ...prev, [assignmentId]: '' }))
      setPointsAdjustments(prev => ({ ...prev, [assignmentId]: 0 }))
      setAdjustmentReasons(prev => ({ ...prev, [assignmentId]: '' }))
      setShowPointAdjuster(prev => ({ ...prev, [assignmentId]: false }))
      setShowSplitPoints(prev => ({ ...prev, [assignmentId]: false }))
      setSplitWithUser(prev => ({ ...prev, [assignmentId]: '' }))
      setSplitPercentage(prev => ({ ...prev, [assignmentId]: 50 }))

      // No need to call loadVerificationData - cache automatically invalidated by mutations
      // Notify parent component to refresh its data (if they haven't migrated to React Query yet)
      onVerificationComplete?.()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to verify chore')
    }

    setVerifyingId('')
  }

  const handlePutBackToNotDone = async (assignmentId: string) => {
    try {
      await updateStatusMutation.mutateAsync({
        assignmentId,
        status: 'assigned',
        notes: 'Chore needs to be redone properly'
      })

      toast.success('Chore put back to assigned status for redo')
      // No need to call loadVerificationData - cache automatically invalidated
      onVerificationComplete?.()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reset chore status')
    }
  }

  const handleUndoVerification = async (assignmentId: string) => {
    if (!confirm('Undo this verification? Points will be removed and the chore will return to completed status.')) return

    try {
      await unverifyMutation.mutateAsync(assignmentId)
      toast.success('Verification undone - points removed')
      // No need to call loadVerificationData - cache automatically invalidated
      onVerificationComplete?.()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to undo verification')
    }
  }

  const handleEditVerification = async (assignmentId: string) => {
    const assignment = verificationHistory.find(a => a.id === assignmentId)
    if (!assignment) return

    const newPoints = editPoints[assignmentId]
    const newNotes = editNotes[assignmentId]
    const newAssignedTo = editAssignedTo[assignmentId]
    const newStatus = editStatus[assignmentId] || assignment.status
    const newCompletionNotes = editCompletionNotes[assignmentId]

    if (!newPoints || newPoints < 0) {
      toast.error('Please enter valid points')
      return
    }

    try {
      // Update assignment fields if changed
      if (newAssignedTo && newAssignedTo !== assignment.assigned_to) {
        await apiClient.put(`/api/assignments/${assignmentId}`, {
          assigned_to: newAssignedTo
        })
      }

      // Update completion notes if changed
      if (newCompletionNotes !== assignment.completion_notes) {
        await apiClient.put(`/api/assignments/${assignmentId}`, {
          notes: newCompletionNotes
        })
      }

      // Handle status changes
      if (newStatus !== 'verified') {
        // If changing to non-verified status, unverify first
        await unverifyMutation.mutateAsync(assignmentId)

        if (newStatus !== 'completed') {
          // Update to new status if not completed
          await updateStatusMutation.mutateAsync({
            assignmentId,
            status: newStatus
          })
        }
      } else {
        // Re-verify with new values
        await unverifyMutation.mutateAsync(assignmentId)
        await verifyMutation.mutateAsync({
          assignmentId,
          approved: true,
          verificationNotes: newNotes || 'Edited verification',
          pointsAwarded: newPoints
        })
      }

      toast.success('Verification updated successfully')
      setEditingId('')
      setEditPoints({})
      setEditNotes({})
      setEditAssignedTo({})
      setEditStatus({})
      setEditCompletionNotes({})
      // No need to call loadVerificationData - cache automatically invalidated by mutations
      onVerificationComplete?.()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update verification')
    }
  }

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)
    
    if (diffHours < 1) return 'Just completed'
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return date.toLocaleDateString()
  }

  const AssignmentCard = ({ assignment, showVerificationControls = false }: {
    assignment: Assignment
    showVerificationControls?: boolean
  }) => {
    const { chore, assigned_user } = assignment
    const isVerifying = verifyingId === assignment.id
    const otherKids = allKids.filter(k => k.id !== assigned_user.id)
    
    return (
      <div className={`card ${categoryColors[chore.category as keyof typeof categoryColors]} border-2`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3 flex-1">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
              style={{ backgroundColor: assigned_user.color_theme }}
            >
              {assigned_user.display_name[0]}
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="font-semibold text-gray-900">{chore.name}</h3>
                <span className="text-sm text-gray-500">by {assigned_user.display_name}</span>
              </div>
              <p className="text-sm text-gray-600">{chore.description}</p>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-xl font-bold text-blue-600">
              {assignment.points_offered}
            </div>
            <div className="text-xs text-gray-500">points</div>
          </div>
        </div>

        {/* Chore Details */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${difficultyColors[chore.difficulty as keyof typeof difficultyColors]}`}>
            {chore.difficulty}
          </span>
          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
            ~{chore.estimated_minutes} min
          </span>
          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
            {chore.category}
          </span>
        </div>

        {/* Date Timeline */}
        <div className="bg-gray-50 rounded-lg p-3 mb-3 space-y-2">
          <div className="text-sm space-y-1">
            {assignment.due_date && (
              <div className="flex items-center gap-2 text-gray-600">
                <CalendarDays className="w-4 h-4 text-gray-400" />
                <span className="font-medium">Due:</span>
                <span>{format(parseISO(assignment.due_date), 'MMM d, yyyy')}</span>
              </div>
            )}
            {assignment.completed_at && (
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="w-4 h-4 text-green-400" />
                <span className="font-medium">Completed:</span>
                <span>{format(parseISO(assignment.completed_at), 'MMM d, yyyy h:mm a')}</span>
                <span className="text-xs text-gray-500">({formatTimeAgo(assignment.completed_at)})</span>
              </div>
            )}
            {assignment.verified_at && (
              <div className="flex items-center gap-2 text-gray-600">
                <CheckCircle2 className="w-4 h-4 text-blue-400" />
                <span className="font-medium">Verified:</span>
                <span>{format(parseISO(assignment.verified_at), 'MMM d, yyyy h:mm a')}</span>
                <span className="text-xs text-gray-500">({formatTimeAgo(assignment.verified_at)})</span>
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        {chore.instructions && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm font-medium text-gray-900 mb-1">Instructions:</div>
            <div className="text-sm text-gray-700">{chore.instructions}</div>
          </div>
        )}

        {/* Completion Notes */}
        {assignment.completion_notes && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <div className="text-sm font-medium text-blue-900 mb-1">Completion Notes:</div>
            <div className="text-sm text-blue-800">"{assignment.completion_notes}"</div>
          </div>
        )}

        {/* Completion Photo */}
        {assignment.completion_photo_url && (
          <div className="mb-4">
            <div className="text-sm font-medium text-gray-900 mb-2">Completion Photo:</div>
            <img
              src={assignment.completion_photo_url}
              alt="Completion verification"
              className="w-full max-w-md mx-auto rounded-lg border-2 border-gray-200"
            />
          </div>
        )}

        {/* Verification Controls */}
        {showVerificationControls && (
          <div className="border-t pt-4 mt-4">
            <div className="space-y-4">
              {/* Quick Award Buttons */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quick Award:
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => {
                      setPointsAdjustments(prev => ({ ...prev, [assignment.id]: assignment.points_offered }))
                      setAdjustmentReasons(prev => ({ ...prev, [assignment.id]: '' }))
                      setShowPointAdjuster(prev => ({ ...prev, [assignment.id]: false }))
                    }}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      (!pointsAdjustments[assignment.id] || pointsAdjustments[assignment.id] === assignment.points_offered)
                        ? 'bg-green-100 text-green-800 border-2 border-green-500'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    ✅ Full Points
                    ({assignment.points_offered} pts)
                  </button>
                  <button
                    onClick={() => {
                      const halfPoints = Math.floor(assignment.points_offered / 2)
                      setPointsAdjustments(prev => ({ ...prev, [assignment.id]: halfPoints }))
                      setShowPointAdjuster(prev => ({ ...prev, [assignment.id]: true }))
                    }}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      pointsAdjustments[assignment.id] === Math.floor(assignment.points_offered / 2)
                        ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-500'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    ⚠️ Partial
                    ({Math.floor(assignment.points_offered / 2)} pts)
                  </button>
                  <button
                    onClick={() => {
                      const bonusPoints = Math.floor(assignment.points_offered * 1.5)
                      setPointsAdjustments(prev => ({ ...prev, [assignment.id]: bonusPoints }))
                      setAdjustmentReasons(prev => ({ ...prev, [assignment.id]: 'Exceptional work - bonus points!' }))
                      setShowPointAdjuster(prev => ({ ...prev, [assignment.id]: true }))
                    }}
                    className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 transition-colors"
                  >
                    🌟 Bonus
                    ({Math.floor(assignment.points_offered * 1.5)} pts)
                  </button>
                </div>
              </div>

              {/* Split Points Toggle */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowSplitPoints(prev => ({ ...prev, [assignment.id]: !prev[assignment.id] }))}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    showSplitPoints[assignment.id] 
                      ? 'bg-blue-100 text-blue-800 border-2 border-blue-500'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  <span>Split Points Between Kids</span>
                </button>
                
                <button
                  onClick={() => setShowPointAdjuster(prev => ({ ...prev, [assignment.id]: !prev[assignment.id] }))}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <Award className="w-4 h-4" />
                  <span>Custom Points</span>
                </button>
              </div>

              {/* Split Points Controls */}
              {showSplitPoints[assignment.id] && (
                <div className="p-3 bg-indigo-50 rounded-lg space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Split points with:
                    </label>
                    <select
                      value={splitWithUser[assignment.id] || ''}
                      onChange={(e) => setSplitWithUser(prev => ({ ...prev, [assignment.id]: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select a helper...</option>
                      {otherKids.map(kid => (
                        <option key={kid.id} value={kid.id}>
                          {kid.display_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Points split percentage:
                    </label>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm">{assigned_user.display_name}:</span>
                      <input
                        type="range"
                        min="10"
                        max="90"
                        step="10"
                        value={splitPercentage[assignment.id] || 50}
                        onChange={(e) => setSplitPercentage(prev => ({ ...prev, [assignment.id]: parseInt(e.target.value) }))}
                        className="flex-1"
                      />
                      <span className="text-sm font-medium">{splitPercentage[assignment.id] || 50}%</span>
                    </div>
                    {splitWithUser[assignment.id] && (
                      <div className="text-sm text-gray-600 mt-1">
                        Helper gets: {100 - (splitPercentage[assignment.id] || 50)}%
                      </div>
                    )}
                  </div>
                  
                  {splitWithUser[assignment.id] && (
                    <div className="bg-white p-2 rounded text-sm">
                      <div>{assigned_user.display_name}: {Math.floor((pointsAdjustments[assignment.id] || assignment.points_offered) * ((splitPercentage[assignment.id] || 50) / 100))} pts</div>
                      <div>
                        {allKids.find(k => k.id === splitWithUser[assignment.id])?.display_name}: {Math.floor((pointsAdjustments[assignment.id] || assignment.points_offered) * ((100 - (splitPercentage[assignment.id] || 50)) / 100))} pts
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Custom Points Adjustment */}
              {showPointAdjuster[assignment.id] && (
                <div className="p-3 bg-blue-50 rounded-lg space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Points Amount:
                    </label>
                    <input
                      type="number"
                      value={pointsAdjustments[assignment.id] || ''}
                      onChange={(e) => setPointsAdjustments(prev => ({ ...prev, [assignment.id]: parseInt(e.target.value) || 0 }))}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      placeholder={assignment.points_offered.toString()}
                      min="0"
                      max="500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reason for Adjustment:
                    </label>
                    <input
                      type="text"
                      value={adjustmentReasons[assignment.id] || ''}
                      onChange={(e) => setAdjustmentReasons(prev => ({ ...prev, [assignment.id]: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      placeholder="Great attention to detail..."
                    />
                  </div>
                </div>
              )}

              {/* Verification Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={verificationNotes[assignment.id] || ''}
                  onChange={(e) => setVerificationNotes(prev => ({ ...prev, [assignment.id]: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md resize-none"
                  rows={2}
                  placeholder="Great job! Keep it up..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => handleVerify(assignment.id, true)}
                  disabled={isVerifying || (showSplitPoints[assignment.id] && !splitWithUser[assignment.id])}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isVerifying ? (
                    <span className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Approving...
                    </span>
                  ) : (
                    '✅ Approve & Award Points'
                  )}
                </button>
                
                <button
                  onClick={() => handlePutBackToNotDone(assignment.id)}
                  disabled={isVerifying}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Back to Not Done</span>
                </button>
                
                <button
                  onClick={() => handleVerify(assignment.id, false)}
                  disabled={isVerifying}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ❌ Reject
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Verified Status */}
        {assignment.status === 'verified' && (
          <div className="border-t pt-4 mt-4">
            {editingId === assignment.id ? (
              // Edit Mode
              <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-blue-900 text-lg">Edit Verification</span>
                  <button
                    onClick={() => {
                      setEditingId('')
                      setEditPoints({})
                      setEditNotes({})
                      setEditAssignedTo({})
                      setEditStatus({})
                      setEditCompletionNotes({})
                    }}
                    className="text-gray-500 hover:text-gray-700 text-xl"
                  >
                    ✕
                  </button>
                </div>
                
                {/* Two column layout for better organization */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Left Column */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Points Awarded:</label>
                      <input
                        type="number"
                        value={editPoints[assignment.id] || assignment.points_earned || assignment.points_offered}
                        onChange={(e) => setEditPoints(prev => ({ ...prev, [assignment.id]: parseInt(e.target.value) || 0 }))}
                        className="w-full p-2 border border-gray-300 rounded-md"
                        min="0"
                        max="500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To:</label>
                      <select
                        value={editAssignedTo[assignment.id] || assignment.assigned_to}
                        onChange={(e) => setEditAssignedTo(prev => ({ ...prev, [assignment.id]: e.target.value }))}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      >
                        <option value={assignment.assigned_to}>
                          {assignment.assigned_user?.display_name || 'Current User'}
                        </option>
                        {allKids.filter(k => k.id !== assignment.assigned_to).map(kid => (
                          <option key={kid.id} value={kid.id}>
                            {kid.display_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status:</label>
                      <select
                        value={editStatus[assignment.id] || assignment.status}
                        onChange={(e) => setEditStatus(prev => ({ ...prev, [assignment.id]: e.target.value }))}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      >
                        <option value="verified">✅ Verified</option>
                        <option value="completed">✓ Completed (Unverified)</option>
                        <option value="pending_verification">⏳ Pending Verification</option>
                        <option value="assigned">📋 Assigned (Not Done)</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Right Column */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Verification Notes:</label>
                      <textarea
                        value={editNotes[assignment.id] || assignment.verification_notes || ''}
                        onChange={(e) => setEditNotes(prev => ({ ...prev, [assignment.id]: e.target.value }))}
                        className="w-full p-2 border border-gray-300 rounded-md resize-none"
                        rows={3}
                        placeholder="Great job! Well done..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Completion Notes:</label>
                      <textarea
                        value={editCompletionNotes[assignment.id] !== undefined ? editCompletionNotes[assignment.id] : assignment.completion_notes || ''}
                        onChange={(e) => setEditCompletionNotes(prev => ({ ...prev, [assignment.id]: e.target.value }))}
                        className="w-full p-2 border border-gray-300 rounded-md resize-none"
                        rows={2}
                        placeholder="Notes from when chore was completed..."
                      />
                    </div>
                  </div>
                </div>
                
                {/* Chore Info Display */}
                <div className="bg-white/50 p-2 rounded text-sm">
                  <div className="text-gray-600">
                    <strong>Chore:</strong> {chore.name} | 
                    <strong> Category:</strong> {chore.category} | 
                    <strong> Difficulty:</strong> {chore.difficulty}
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEditVerification(assignment.id)}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium"
                  >
                    💾 Save All Changes
                  </button>
                  <button
                    onClick={() => {
                      setEditingId('')
                      setEditPoints({})
                      setEditNotes({})
                      setEditAssignedTo({})
                      setEditStatus({})
                      setEditCompletionNotes({})
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div className="bg-green-100 text-green-800 px-3 py-2 rounded-lg text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium">✅ Verified & Points Awarded!</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{assignment.points_earned || assignment.points_offered} pts</span>
                    <button
                      onClick={() => {
                        setEditingId(assignment.id)
                        setEditPoints(prev => ({ ...prev, [assignment.id]: assignment.points_earned || assignment.points_offered }))
                        setEditNotes(prev => ({ ...prev, [assignment.id]: assignment.verification_notes || '' }))
                        setEditAssignedTo(prev => ({ ...prev, [assignment.id]: assignment.assigned_to }))
                        setEditStatus(prev => ({ ...prev, [assignment.id]: assignment.status }))
                        setEditCompletionNotes(prev => ({ ...prev, [assignment.id]: assignment.completion_notes || '' }))
                      }}
                      className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                      title="Edit verification"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleUndoVerification(assignment.id)}
                      className="p-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                      title="Undo verification"
                    >
                      <Undo2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {assignment.verification_notes && (
                  <div className="mt-2 p-2 bg-white/50 rounded text-green-900">
                    📝 {assignment.verification_notes}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // Show access denied if not a parent
  if (!user?.is_parent) {
    return (
      <div className="card text-center py-12">
        <div className="text-6xl mb-4">🔒</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Parents Only
        </h2>
        <p className="text-gray-600 max-w-md mx-auto">
          Only parents can access the chore verification system.
        </p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Loading verifications...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'pending'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          ⏳ Pending Verification ({pendingVerifications.length})
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'history'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          📋 Verification History
        </button>
      </div>

      {/* Pending Verifications Tab */}
      {activeTab === 'pending' && (
        <div>
          <h2 className="text-xl font-semibold mb-4">
            ⏳ Chores Awaiting Your Approval
          </h2>
          
          {pendingVerifications.length === 0 ? (
            <div className="card text-center py-8">
              <div className="text-4xl mb-2">🎉</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                All caught up!
              </h3>
              <p className="text-gray-600">
                No chores are currently waiting for verification.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {pendingVerifications.map((assignment) => (
                <AssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  showVerificationControls={true}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Verification History Tab */}
      {activeTab === 'history' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              📋 Recent Verifications
            </h2>
            
            {/* Date Filter Buttons */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setDateFilter('today')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    dateFilter === 'today' 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => setDateFilter('week')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    dateFilter === 'week' 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Week
                </button>
                <button
                  onClick={() => setDateFilter('month')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    dateFilter === 'month' 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Month
                </button>
                <button
                  onClick={() => setDateFilter('all')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    dateFilter === 'all' 
                      ? 'bg-white text-blue-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  All
                </button>
              </div>
            </div>
          </div>
          
          {/* Sorting and User Filter Controls */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-600">
                Showing {filteredHistory.length} of {verificationHistory.length} verifications
              </div>
              
              {/* User Filter */}
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500" />
                <select
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Kids</option>
                  {allKids.map(kid => (
                    <option key={kid.id} value={kid.id}>
                      {kid.display_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="verified_date">Verified Date</option>
                <option value="due_date">Due Date</option>
                <option value="points">Points</option>
                <option value="user">User</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                title={`Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}`}
              >
                <ArrowUpDown className="w-4 h-4" />
                {sortOrder === 'desc' ? '↓' : '↑'}
              </button>
            </div>
          </div>
          
          {filteredHistory.length === 0 ? (
            <div className="card text-center py-8">
              <div className="text-4xl mb-2">📋</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No verifications found
              </h3>
              <p className="text-gray-600">
                {(() => {
                  const selectedKid = userFilter !== 'all' ? allKids.find(k => k.id === userFilter) : null
                  const userText = selectedKid ? ` for ${selectedKid.display_name}` : ''
                  
                  if (dateFilter === 'today') return `No verifications today${userText}`
                  if (dateFilter === 'week') return `No verifications this week${userText}`
                  if (dateFilter === 'month') return `No verifications this month${userText}`
                  if (dateFilter === 'all') return `No verification history${userText}`
                  return 'No verifications found'
                })()}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredHistory.map((assignment) => (
                <AssignmentCard
                  key={assignment.id}
                  assignment={assignment}
                  showVerificationControls={false}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}